import React, { useContext, useMemo } from 'react';
import { get, merge, set } from 'lodash';
import styled from 'styled-components';

import ActionButton from 'src/presentation/components/pages/sidekick/threads/components/ThreadBuilder/ActionButton';
import Comparator from './Comparator';
import ConditionInput from './ConditionInput';
import ConditionOperator from './ConditionOperator';
import FormContext from 'src/presentation/contexts/FormContext';
import { HalfRow } from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/Components/controls/Layout';
import MemoSelector, { MemorySelectorResult } from './MemorySelector';
import { OptionContainer } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import { SelectOption } from './helpers/memoriesHelper';
import TrashIcon from '@ad-scripts/presentation/components/common/icons/trash';
import { useCondition } from './useCondition';

type ConditionProps = {
  attr: string;
  attrList: string;
  disabled: boolean;
  id: string;
  index: number;
  removeOptionIdByIndex: (index: number) => void;
  notChangeableFields: string[];
};

const Condition = ({
  attrList,
  disabled,
  attr,
  id,
  index,
  removeOptionIdByIndex,
  notChangeableFields,
}: ConditionProps) => {
  const { workingState, onBlur } = useContext(FormContext);

  const baseAttr = `${attr}.if`;

  const joinAttr = `${attrList}[${index - 1}].join`;

  const fields = useMemo(() => {
    const fields = get(workingState, `${baseAttr}.fields`) || [];
    if (notChangeableFields?.length) {
      return merge(fields, notChangeableFields);
    }

    return fields;
  }, [baseAttr, notChangeableFields, workingState]);

  const { currentFields, setCurrentFields, optGroups, comparatorOptions, lastSpec } = useCondition({
    fields,
    allowEnterManually: false,
    compatibilityMatch: true,
    includeOnlyNestedProps: false,
  });

  const comparator = get(workingState, `${baseAttr}.comparator`) as unknown;

  const handleChangeMemoFields = props => {
    const currentProperties = props.currentState;
    const newProps = { currentState: set(currentProperties, `${baseAttr}.rhs`, null) };

    onBlur && onBlur(newProps);
  };

  const handleChangeComparator = props => {
    const currentProperties = props.currentState;
    const newProps = { currentState: set(currentProperties, `${baseAttr}.rhs`, null) };

    onBlur && onBlur(newProps);
  };

  const ActionRow = ({ index, onDelete }: { index: number; onDelete }) => {
    if (index === 0) {
      return null;
    }

    return (
      <StyledActionRow>
        <ActionButton disabled={disabled} data-aid="delete-button" onClick={onDelete} title="Delete">
          <TrashIcon width="16" alt="delete" />
        </ActionButton>
      </StyledActionRow>
    );
  };

  return (
    <StyledOptionContainer
      actionRow={null}
      attrList={attrList}
      currentIndex={index}
      id={id}
      removeOptionIdByIndex={removeOptionIdByIndex}
      role="listitem"
    >
      <StyledConditionOperator attr={joinAttr} className="condition-operator" index={index} disabled={disabled} />
      <StyledRow>
        <MemoSelector
          attr={`${baseAttr}.fields`}
          disabled={disabled}
          isEnterManually={false}
          optGroups={optGroups}
          fields={currentFields}
          setCurrentFields={setCurrentFields}
          onChange={handleChangeMemoFields}
          resultType={MemorySelectorResult.ARRAY}
          notChangeableFields={notChangeableFields}
        />
        <Comparator
          attr={`${baseAttr}.comparator`}
          data-aid="comparator"
          disabled={disabled}
          comparatorOptions={comparatorOptions as SelectOption[]}
          onChange={handleChangeComparator}
        />
        <ConditionInput
          attr={`${baseAttr}.rhs`}
          data-aid="condition-input"
          disabled={disabled}
          comparator={comparator as any}
          typeSpec={lastSpec}
        />
        <ActionRow index={index} onDelete={() => removeOptionIdByIndex(index)} />
      </StyledRow>
    </StyledOptionContainer>
  );
};

const StyledActionRow = styled.div`
  flex: 0 0 auto;
  margin-right: auto;
  visibility: hidden;
`;

const StyledRow = styled.div`
  align-items: center;
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: start;
  width: 100%;
`;

const StyledOptionContainer = styled(OptionContainer)`
  background-color: transparent;
  display: flex;
  flex-direction: row;

  & > div:nth-child(1) {
    width: 68px;
  }

  ${HalfRow} {
    margin: 0;
    width: auto;
  }

  &:hover {
    ${StyledActionRow} {
      visibility: visible;
    }
  }
`;

const StyledConditionOperator = styled(ConditionOperator)``;

export default Condition;
